import axios, { AxiosError } from "axios"
import type { AxiosInstance } from "axios"
import { useAuthStore, auth } from "@/models/auth"

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
})

const timeoutRequestData: any = {}

let isRefreshing = false
let failedQueue: {
  resolve: (value?: unknown) => void
  reject: (reason?: any) => void
}[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const resetTimeoutRequest = (name: string, success = false) => {
  if (name === '' || name === '/user/v1/status')
    return
  
  const authStore = useAuthStore()
  if (success) {
    if (timeoutRequestData[name]) {
      clearInterval(timeoutRequestData[name])
      delete timeoutRequestData[name]
    }
  } else {
    if (!timeoutRequestData[name]) {
      timeoutRequestData[name] = setInterval(() => {
        authStore.resetTimeout()
      }, 5 * 1000 * 60)
    }
  }
}

api.interceptors.request.use(
  (config) => {
    const token = auth.getAuth()?.token
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    resetTimeoutRequest(config.url || '')
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    resetTimeoutRequest(response.config?.url || '', true)
    return response
  },
  async (error) => {
    if (error?.response && error?.response.status === 401) {
      const originalRequest = error.config as any & {
        _retry?: boolean
      }

      if (!originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              if (originalRequest.headers)
                originalRequest.headers['Authorization'] = `Bearer ${token}`
              return api(originalRequest)
            })
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const authData = auth.getAuth()
          const refresh_token = authData.refresh_token
          
          const res = await axios.post(
            `${import.meta.env.VITE_BASE_URL}/user/v1/refresh-token`,
            {
              refresh_token,
            }
          )

          const newToken = res.data.data.access_token
          console.log(newToken)
          auth.setAuth({
            ...authData,
            token: newToken,
          })
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          processQueue(null, newToken)
          return api(originalRequest)
        } catch (err) {
          await auth.logout()
          window.location.reload()
          return
        } finally {
          isRefreshing = false
        }
      }
      
      await auth.logout()
      window.location.reload()
      return Promise.reject(error)
    }

    resetTimeoutRequest(error.config?.url || '', true)
    return Promise.reject(error)
  }
)


export const apiPrinter: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_PRINTER_URL || "http://localhost:6001/v1",
  headers: {
    "x-kong-jwt-claim-superadmin_id" : "000000000000000000000000",
  }
})